import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errMsg, useApi } from "@/lib/api";
import { rm } from "@/pages/AkaunPage";

const SIRI = [["payg", "Per Use", "var(--q)"], ["asas50", "50 carian", "var(--k)"], ["tanpahad", "Tanpa had", "var(--i)"]];
const BULAN = ["Jan", "Feb", "Mac", "Apr", "Mei", "Jun", "Jul", "Ogo", "Sep", "Okt", "Nov", "Dis"];
const label = (k) => `${BULAN[Number(k.slice(5)) - 1]} ${k.slice(2, 4)}`;

const sum = (rows, f) => rows.reduce((a, r) => a + r[f], 0);

export const RevenueReport = () => {
  const api = useApi();
  const [rows, setRows] = useState(null);
  useEffect(() => { api("get", "/admin/billing/report").then(setRows).catch(() => setRows([])); }, [api]);
  const [dl, setDl] = useState(false);
  const download = async () => {
    setDl(true);
    try {
      const blob = await api("get", "/admin/billing/report.xlsx", null, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `laporan-hasil-ATAS-${rows[rows.length - 1].bulan}.xlsx`; a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); } finally { setDl(false); }
  };
  if (!rows) return <Loader2 className="animate-spin text-q" />;
  const chart = rows.map((r) => ({ bulan: label(r.bulan), ...Object.fromEntries(SIRI.map(([k]) => [k, r[`${k}_sen`] / 100])) }));
  return (
    <div className="space-y-6" data-testid="admin-report">
      <div className="flex justify-end">
        <button onClick={download} disabled={dl} data-testid="report-download-xlsx"
          className="h-10 px-5 rounded-full bg-ink text-bg text-sm font-semibold inline-flex items-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-40">
          {dl ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Muat turun Excel
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <Stat t="Jumlah 12 bulan" v={rm(sum(rows, "jumlah_sen"))} id="report-total" />
        {SIRI.map(([k, l]) => <Stat key={k} t={l} v={rm(sum(rows, `${k}_sen`))} sub={`${sum(rows, `${k}_bil`)} transaksi`} id={`report-total-${k}`} />)}
      </div>
      <div className="surface rounded-xl border border-line p-4 h-80" data-testid="report-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
            <XAxis dataKey="bulan" tick={{ fontSize: 12 }} stroke="var(--mute)" />
            <YAxis tick={{ fontSize: 12 }} stroke="var(--mute)" tickFormatter={(v) => `RM${v}`} />
            <Tooltip cursor={{ fill: "var(--line)", opacity: 0.35 }} formatter={(v) => `RM${Number(v).toFixed(2)}`} />
            <Legend />
            {SIRI.map(([k, l, c]) => <Bar key={k} dataKey={k} name={l} stackId="a" fill={c} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="rounded-xl border border-line overflow-x-auto surface">
        <Table>
          <TableHeader><TableRow><TableHead>Bulan</TableHead>{SIRI.map(([k, l]) => <TableHead key={k} className="text-right">{l}</TableHead>)}<TableHead className="text-right">Jumlah</TableHead></TableRow></TableHeader>
          <TableBody>
            {[...rows].reverse().map((r) => (
              <TableRow key={r.bulan} data-testid={`report-row-${r.bulan}`}>
                <TableCell className="text-sm">{label(r.bulan)}</TableCell>
                {SIRI.map(([k]) => <TableCell key={k} className="text-right font-mono-data text-sm">{rm(r[`${k}_sen`])}<div className="text-xs text-mute">{r[`${k}_bil`]} trans.{k === "payg" && ` · ${r.carian_payg} carian`}</div></TableCell>)}
                <TableCell className="text-right font-mono-data text-sm font-semibold">{rm(r.jumlah_sen)}<div className="text-xs text-mute font-normal">{r.jumlah_bil} trans.</div></TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter><TableRow><TableCell>Jumlah</TableCell>{SIRI.map(([k]) => <TableCell key={k} className="text-right font-mono-data">{rm(sum(rows, `${k}_sen`))}</TableCell>)}<TableCell className="text-right font-mono-data">{rm(sum(rows, "jumlah_sen"))}</TableCell></TableRow></TableFooter>
        </Table>
      </div>
      <p className="text-xs text-mute">Termasuk semua bayaran sebenar yang berjaya (automatik dan diaktifkan manual). Bayaran mod ujian tidak dikira. Bulan mengikut waktu Malaysia.</p>
    </div>
  );
};

const Stat = ({ t, v, sub, id }) => (
  <div className="surface rounded-xl border border-line p-4" data-testid={id}>
    <div className="eyebrow">{t}</div>
    <div className="mt-2 font-display text-2xl text-ink">{v}</div>
    {sub && <div className="text-xs text-mute mt-1">{sub}</div>}
  </div>
);
